import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, BookOpen, Calculator, Users, Star, Award, Globe, Clock, MapPin, Mail, Phone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function AboutPage() {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <nav className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-tutoring-1/artifacts/0zn1n7em_1000075255.png" 
                alt="ESL Elite Logo" 
                className="h-12 w-auto"
              />
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-sm font-semibold hover:text-[#002FA7]">Home</Link>
              <Link to="/about" className="text-sm font-semibold text-[#002FA7]">About Us</Link>
              <a href="/#services" className="text-sm font-semibold hover:text-[#002FA7]">Services</a>
              <a href="/#contact" className="text-sm font-semibold hover:text-[#002FA7]">Contact</a>
              {user ? (
                <>
                  <Link
                    to={user.role === 'admin' ? '/admin' : user.role === 'tutor' ? '/tutor' : '/student'}
                    className="text-sm font-semibold hover:text-[#002FA7]"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={logout}
                    className="text-sm font-semibold px-4 py-2 rounded-md border border-slate-900 hover:bg-slate-900 hover:text-white"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-sm font-semibold hover:text-[#002FA7]">Login</Link>
                  <Link
                    to="/register"
                    className="text-sm font-semibold px-4 py-2 rounded-md bg-[#002FA7] text-white hover:bg-[#001a5c]"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-6" data-testid="about-heading">About Us</h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
              At Elite French-English & Math Tutoring, we believe every student deserves access to world-class education. Our mission is to empower learners of all ages to achieve their academic goals through personalized, expert instruction.
            </p>
          </div>

          <div className="bg-white p-8 border border-slate-200 mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#002FA7]">Our Mission</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We are dedicated to providing high-quality, personalized tutoring that makes a real difference in our students' lives. Whether you're a child learning your first words in a new language, a high school student striving for top grades in math, or an adult looking to master English or French as a second language, we are here to guide you every step of the way.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Our approach combines proven teaching methodologies with modern technology, offering both online and in-person sessions to fit your lifestyle. We don't just teach subjects - we build confidence, develop critical thinking skills, and inspire a love for learning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 border border-slate-200">
              <h2 className="text-2xl font-bold mb-6 text-[#002FA7]">What We Teach</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#002FA7] rounded-md flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">French Language</h3>
                    <p className="text-sm text-slate-600">K-12 curriculum and FSL (French as a Second Language) for adults. Grammar, conversation, reading, and writing.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#E32636] rounded-md flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">English ESL</h3>
                    <p className="text-sm text-slate-600">K-12 English and ESL (English as a Second Language) for adults. TOEFL, IELTS prep, and business English.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#FFC000] rounded-md flex items-center justify-center flex-shrink-0">
                    <Calculator className="w-5 h-5 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Mathematics</h3>
                    <p className="text-sm text-slate-600">Grades 1-12, from elementary arithmetic to advanced calculus. Personalized math instruction.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#002FA7] rounded-md flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Haitian Creole</h3>
                    <p className="text-sm text-slate-600">Learn Haitian Creole with a native-speaking instructor. All levels welcome.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 border border-slate-200">
              <h2 className="text-2xl font-bold mb-6 text-[#002FA7]">Why Choose Us</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500 rounded-md flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Personalized Approach</h3>
                    <p className="text-sm text-slate-600">One-on-one sessions tailored to your unique learning style, pace, and goals.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500 rounded-md flex items-center justify-center flex-shrink-0">
                    <Star className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Proven Results</h3>
                    <p className="text-sm text-slate-600">98% of our students see grade improvements within 3 months of starting.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500 rounded-md flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Certified Tutors</h3>
                    <p className="text-sm text-slate-600">All tutors hold advanced degrees and teaching certifications with 10+ years experience.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500 rounded-md flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Online & In-Person</h3>
                    <p className="text-sm text-slate-600">Flexible sessions available via Zoom (with instant link delivery) or face-to-face.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 border border-slate-200 mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#002FA7]">Flexible Pricing</h2>
            <p className="text-slate-600 mb-6">We offer competitive rates with session lengths to fit every schedule and budget:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-[#F3F4F6] p-6 border border-slate-200 text-center">
                <Clock className="w-6 h-6 text-[#002FA7] mx-auto mb-2" />
                <div className="text-3xl font-black text-[#002FA7]">$40</div>
                <div className="text-sm font-semibold text-slate-600 mt-1">30 Minutes</div>
              </div>
              <div className="bg-[#F3F4F6] p-6 border border-slate-200 text-center">
                <Clock className="w-6 h-6 text-[#002FA7] mx-auto mb-2" />
                <div className="text-3xl font-black text-[#002FA7]">$55</div>
                <div className="text-sm font-semibold text-slate-600 mt-1">45 Minutes</div>
              </div>
              <div className="bg-[#F3F4F6] p-6 border border-slate-200 text-center">
                <Clock className="w-6 h-6 text-[#002FA7] mx-auto mb-2" />
                <div className="text-3xl font-black text-[#002FA7]">$60</div>
                <div className="text-sm font-semibold text-slate-600 mt-1">60 Minutes</div>
              </div>
              <div className="bg-[#F3F4F6] p-6 border border-slate-200 text-center">
                <Clock className="w-6 h-6 text-[#002FA7] mx-auto mb-2" />
                <div className="text-3xl font-black text-[#002FA7]">$90</div>
                <div className="text-sm font-semibold text-slate-600 mt-1">90 Minutes</div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 border border-slate-200 mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#002FA7]">Payment Options</h2>
            <p className="text-slate-600 mb-4">We accept payments from students around the world through multiple channels:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-[#F3F4F6] p-4 border border-slate-200 text-center">
                <div className="font-bold">Stripe</div>
                <div className="text-xs text-slate-600 mt-1">Credit & Debit Cards</div>
              </div>
              <div className="bg-[#F3F4F6] p-4 border border-slate-200 text-center">
                <div className="font-bold">PayPal</div>
                <div className="text-xs text-slate-600 mt-1">Worldwide Payments</div>
              </div>
              <div className="bg-[#F3F4F6] p-4 border border-slate-200 text-center">
                <div className="font-bold">Interac</div>
                <div className="text-xs text-slate-600 mt-1">Canadian e-Transfers</div>
              </div>
              <div className="bg-[#F3F4F6] p-4 border border-slate-200 text-center">
                <div className="font-bold">Western Union</div>
                <div className="text-xs text-slate-600 mt-1">International Transfers</div>
              </div>
            </div>
          </div>

          <div className="bg-[#002FA7] text-white p-8 rounded-md text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to Start Learning?</h2>
            <p className="text-white/80 mb-6">
              Join hundreds of students who have achieved their academic goals with us.
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              <Link
                to="/register"
                className="px-6 py-3 bg-white text-[#002FA7] font-bold rounded-md hover:bg-slate-100"
                data-testid="about-cta-button"
              >
                Book a Session
              </Link>
              <a
                href="tel:+12269203856"
                className="px-6 py-3 border-2 border-white text-white font-bold rounded-md hover:bg-white/10"
              >
                Call Us: (226) 920-3856
              </a>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-center mb-6">Available Worldwide</h2>
            <div className="rounded-md overflow-hidden border border-slate-200">
              <iframe
                title="Elite French-English & Math Tutoring - Available Worldwide"
                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d40000000!2d-40!3d30!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f2!5e0!3m2!1sen!2sca!4v1700000000000!5m2!1sen!2sca"
                width="100%"
                height="350"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="about-world-map"
              ></iframe>
              <div className="bg-[#002FA7] text-white text-center py-3 text-sm font-semibold">
                Serving Students Worldwide - Online & In-Person Sessions Available
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-tutoring-1/artifacts/0zn1n7em_1000075255.png" 
                alt="ESL Elite Logo" 
                className="h-10 w-auto mb-4"
              />
              <p className="text-sm text-slate-400">
                Professional tutoring in French, English, and Math for students of all ages.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-3">Quick Links</h4>
              <div className="space-y-2">
                <Link to="/" className="block text-sm text-slate-400 hover:text-white">Home</Link>
                <Link to="/about" className="block text-sm text-slate-400 hover:text-white">About Us</Link>
                <Link to="/register" className="block text-sm text-slate-400 hover:text-white">Book a Session</Link>
                <Link to="/login" className="block text-sm text-slate-400 hover:text-white">Student Login</Link>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-3">Contact Us</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Mail className="w-4 h-4" /> eslelitetutoring@gmail.com
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Phone className="w-4 h-4" /> +1 (226) 920-3856
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Phone className="w-4 h-4" /> +1 (437) 677-6164
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <MapPin className="w-4 h-4" /> Online & In-Person
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-700 pt-8 text-center text-sm text-slate-400">
            © 2026 Elite French-English & Math Tutoring. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
